import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { occurrenceCounts } from '@core/services/gbe-error-parser/occurrence-counts';
import { ICONS } from '@core/services/icons/icons.service';
import { PlanEditSessionActions as Session } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectCanRedo,
  selectCanUndo,
  selectIsEditingHere,
  selectPlanEditSession
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlanWatch } from '@core/store/plan-edit/plan-edit.selectors';

import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { Shortcut } from '@shared/utils/shortcut';

import { PlanPanelService } from '../../plan-panel/plan-panel.service';
import {
  PLAN_EDIT_REDO_SHORTCUT,
  PLAN_EDIT_UNDO_SHORTCUT
} from '../plan-edit-dialogs/plan-edit-dialogs.component';
import { PlanEditDialogsService } from '../plan-edit-dialogs/plan-edit-dialogs.service';

const noop = () => undefined;

/** Stands in for the holder banner while this tab edits the plan. */
@Component({
  selector: 'app-plan-edit-bar',
  imports: [TooltipComponent, TranslatePipe],
  templateUrl: './plan-edit-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``
})
export class PlanEditBarComponent {
  readonly planId = input<string | null>(null);

  private readonly store = inject(Store);
  private readonly dialogs = inject(PlanEditDialogsService);
  protected readonly planPanel = inject(PlanPanelService);
  private readonly state = this.store.selectSignal((state: object) => state);
  private readonly watch = this.store.selectSignal(selectPlanWatch);

  protected readonly ICONS = ICONS;
  protected readonly session = this.store.selectSignal(selectPlanEditSession);
  protected readonly canUndo = this.store.selectSignal(selectCanUndo);
  protected readonly canRedo = this.store.selectSignal(selectCanRedo);
  protected readonly undoKeys = new Shortcut(PLAN_EDIT_UNDO_SHORTCUT, noop).toString();
  protected readonly redoKeys = new Shortcut(PLAN_EDIT_REDO_SHORTCUT, noop).toString();

  protected readonly editingHere = computed(() => {
    const planId = this.planId();
    return planId !== null && selectIsEditingHere(planId)(this.state());
  });
  protected readonly mode = computed(() => this.session().mode);
  protected readonly unsavedCount = computed(() => this.session().unsavedCount);
  /** Null until the watch holds this plan's findings: none yet is not no findings. */
  private readonly knownFindings = computed(() => {
    const watch = this.watch();
    return watch.planId === this.planId() ? watch.findings : null;
  });
  protected readonly findingsUnknown = computed(() => this.knownFindings() === null);
  private readonly counts = computed(() => occurrenceCounts(this.knownFindings() ?? []));
  protected readonly findingCount = computed(() => this.counts().total);
  protected readonly blockingCount = computed(() => this.counts().blocking);
  /** Discard and Done end a lost session locally, so they stay offered while disconnected. */
  protected readonly canClose = computed(
    () => this.mode() === 'editing' || this.mode() === 'disconnected'
  );

  protected undo(): void {
    this.store.dispatch(Session.undoRequested());
  }

  protected redo(): void {
    this.store.dispatch(Session.redoRequested());
  }

  protected save(): void {
    if (this.blockingCount() > 0) this.dialogs.findings.set('save');
    else this.store.dispatch(Session.saveRequested());
  }

  protected discard(): void {
    this.dialogs.discard.set(true);
  }

  protected done(): void {
    this.store.dispatch(Session.doneRequested());
  }
}
